import { useCallback, useEffect, useState } from 'react'

export function useLocalStorage(prefix: string, internalKey: string, defaultValue: unknown) {
  return useStorage(asStorageKey(prefix, internalKey), defaultValue, globalThis.localStorage)
}

export function useSessionStorage(prefix: string, internalKey: string, defaultValue: unknown) {
  return useStorage(asStorageKey(prefix, internalKey), defaultValue, globalThis.sessionStorage)
}

export function useMultiWindowAwareLocalStorage(prefix: string, internalKey: string, defaultValue: unknown) {
  return useMultiWindowAwareStorage(asStorageKey(prefix, internalKey), defaultValue, globalThis.localStorage)
}

type StorageKey = {
  prefix: string
  internalKey: string
  fullKey: string
}

function asStorageKey(prefix: string, internalKey: string) {
  return {
    prefix,
    internalKey,
    fullKey: `${prefix}-${internalKey}`
  }
}

function getStorageValue(key: StorageKey, storageObject: Storage) {
  const jsonValue = storageObject.getItem(key.fullKey)
  return jsonValue ? JSON.parse(jsonValue) : jsonValue
}

function useStorage(key: StorageKey, defaultValue: unknown, storageObject: Storage) {
  const [value, setValue] = useState(() => {
    const storedValue = getStorageValue(key, storageObject)
    if (storedValue != null) return storedValue

    if (typeof defaultValue === 'function') {
      return defaultValue()
    } else {
      return defaultValue
    }
  })

  useEffect(() => {
    const jsonValue = storageObject.getItem(key.fullKey)
    const newJsonValue = value ? JSON.stringify(value) : null

    if (jsonValue !== newJsonValue) {
      // force send event local
      globalThis.dispatchEvent(new StorageEvent('storage', { key: key.fullKey, newValue: value }))
      if (!value) {
        return storageObject.removeItem(key.fullKey)
      }
      // biome-ignore lint/style/noNonNullAssertion: checked by !value
      storageObject.setItem(key.fullKey, newJsonValue!)
    }
  }, [key, value, storageObject])

  const remove = useCallback(() => {
    setValue(undefined)
  }, [])

  return [value, setValue, remove]
}

function useMultiWindowAwareStorage(key: StorageKey, defaultValue: unknown, storageObject: Storage) {
  const [value, setValue, remove] = useStorage(key, defaultValue, storageObject)

  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key === key.fullKey) {
        // queue action due to storage update timing across tabs/windows
        setTimeout(() => setValue(getStorageValue(key, storageObject)), 0)
      }
    }
    globalThis.addEventListener('storage', handler)
    return () => {
      globalThis.removeEventListener('storage', handler)
    }
  }, [key, setValue, storageObject])

  return [value, setValue, remove]
}
